import { html, render, nothing} from "lit-html";
import { unsafeHTML } from "lit-html/directives/unsafe-html";
import { createRef, ref } from "lit-html/directives/ref";
import { determineTranslation } from "../../../utils/language.js";
import { ProgressBar } from "../../../common/progress-bar.js";
import "../../../common/progress-bar.js";
import { format } from '../../../utils/string.js';
import { CmsTooltip, TooltipPlacement } from "../../../website/cms-tooltip.js";
import { IOrderDiscount } from "../../../api/estore/discount/interfaces/order-discount.js";
import { OrderDiscountApi } from "../../../api/estore/discount/order-discount-api.js";

export class OrderDiscountProgressBar extends HTMLElement {
    ///TODO:
    /// Use number formating from number.ts (test with decimals). Did not found it useful in this specific component.

    constructor() {
        super();
        this.labels = determineTranslation(this.translations);
        this.orderDiscountLines = null;
        this.eventHandlers();        
    }

    /* #region Public */
    public setVisibility(visible: boolean): void {
        if(visible) {
            this.removeAttribute("hidden")
        } else {
            this.setAttribute("hidden", "");
        }
    }
    /* #endregion */

    /* #region Translations */
    private translations: { [key:string]: {[key:string]: string} } = {
        en: {
            nextGoal: "<strong>{0}</strong> left till {1}% discount",
            goalReached: "You got {0}% discount",
            totalSum: "Total sum",
            discountTableTitle: "Discount",
        },
        no: {
            nextGoal: "<strong>{0}</strong> igjen til {1}% rabatt",
            goalReached: "Du fikk {0}% rabatt",
            totalSum: "Total sum",
            discountTableTitle: "Rabatt",
        }
    };
    /* #endregion */

    /* #region Markup */
    private template = () => this.orderDiscountLines.length ? html`
        <div class="next-goal ${this.nextGoal ? 'in-progress': 'is-last'}">
            ${this.nextGoal ? html`
                <div class="text">${unsafeHTML(format(this.labels.nextGoal, this.nextGoal.amountUntilAppliesFormated, this.nextGoal.discountPercent.toString()))}</div>
                <button class="btn-show-order-discount-tooltip btn" type="button">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12">
                        <path id="ue91e-info" d="M6,0a6,6,0,1,0,6,6A6,6,0,0,0,6,0ZM7.249,9.3q-.463.183-.739.278a1.949,1.949,0,0,1-.641.1A1.277,1.277,0,0,1,5,9.4a.883.883,0,0,1-.31-.694,2.522,2.522,0,0,1,.023-.335c.016-.114.041-.242.075-.386l.387-1.365c.034-.131.063-.254.087-.371a1.646,1.646,0,0,0,.035-.322.5.5,0,0,0-.108-.364.609.609,0,0,0-.413-.1,1.084,1.084,0,0,0-.307.046c-.1.032-.195.061-.269.089l.1-.421q.379-.152.726-.265A2.146,2.146,0,0,1,5.678,4.8a1.254,1.254,0,0,1,.859.269.894.894,0,0,1,.3.7q0,.089-.021.313a2.1,2.1,0,0,1-.077.412L6.357,7.852c-.031.109-.059.234-.085.374a1.977,1.977,0,0,0-.037.318.457.457,0,0,0,.121.37.68.68,0,0,0,.42.1,1.219,1.219,0,0,0,.318-.051,1.808,1.808,0,0,0,.257-.086ZM7.181,3.773a.918.918,0,0,1-.648.25.927.927,0,0,1-.65-.25.817.817,0,0,1,0-1.215.92.92,0,0,1,.65-.254.91.91,0,0,1,.648.254.818.818,0,0,1,0,1.213Z" fill="#222226"/>
                    </svg>
                </button>
                ${this.buildTooltip()}
            ` : html`
                <svg xmlns="http://www.w3.org/2000/svg" width="11.501" height="8.002" viewBox="0 0 11.501 8.002">
                    <path id="check2" d="M13.854,3.646a.5.5,0,0,1,0,.708l-7,7a.5.5,0,0,1-.708,0l-3.5-3.5a.5.5,0,0,1,.708-.708L6.5,10.293l6.646-6.647a.5.5,0,0,1,.708,0Z" transform="translate(-2.499 -3.499)" fill="#75aa83"/>
                </svg>
                <div class="text">${unsafeHTML(format(this.labels.goalReached, this.maximumOrderDiscount.discountPercent.toString()))}</div>
            ` }
        </div>
        <progress-bar ${ref(this.progressBar)}></progress-bar>
        ` : nothing;
    /* #endregion */

    /* #region Private */
    private api = new OrderDiscountApi();
    private orderDiscountLines: IOrderDiscount[];
    private nextGoal: IOrderDiscount;
    private maximumOrderDiscount: IOrderDiscount;
    private progressBar = createRef<ProgressBar>();
    protected labels: { [key: string]:any };

    private async load(): Promise<void> {
        this.orderDiscountLines = await this.api.getOrderDiscounts();
        this.orderDiscountLines.sort((a, b) => a.discountPercent - b.discountPercent);
        this.maximumOrderDiscount = this.orderDiscountLines.slice(-1)[0];

        this.nextGoal = this.orderDiscountLines.find((element: IOrderDiscount) => {
            if (element.willApply == false)
            return element;
        });

        render(this.template(), this);

        if (this.progressBar.value){
            this.progressBar.value.percent = this.nextGoal ? this.nextGoal.progress : 100;
        }
    }

    private buildTooltip() {
        const tooltipBody = document.createElement("table");
        tooltipBody.classList.add("order-discount-table");
        const tooltipLitMarkup = html`
                <thead>
                    <th>${this.labels.totalSum}</th>
                    <th>${this.labels.discountTableTitle}</th>
                </thead>
                <tbody>
                    ${this.orderDiscountLines.map(line => html`<tr>
                        <td class="discount-name">${line.name}</td>
                        <td class="discount-percent" align="right">${line.discountPercent}%</td>
                    </tr>`)}
                </tbody>
            `;

        render(tooltipLitMarkup, tooltipBody);

        const discountDetailsTooltip = new CmsTooltip({
            placement: TooltipPlacement.Top,
            context: this,
            triggerSelector: ".btn-show-order-discount-tooltip",
            bodyTemplate: tooltipBody,
        });

        return discountDetailsTooltip;
    }

    private eventHandlers(){
        window.PubSub.subscribe('estore.callback.shopcart',  (ignore: any, initDetails: any) => {
            if(initDetails.status === "Ok"){
                this.load();
            }
        });
    }

    /* #endregion */
    async connectedCallback() {
        await this.load();
    }
}

customElements.define("order-discount-progress-bar", OrderDiscountProgressBar);